import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback, useMemo, useState } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { supabase } from '../lib/supabase'
import type { TrainingWeek } from '../types/database.tables'
import { formatDateForComparison } from '../utils/dateUtils'

export interface UseTrainingWeeksReturn {
  trainingWeek: TrainingWeek | null
  isLoading: boolean
  error: string | null
  loadTrainingWeek: (startDate: Date) => Promise<void>
  updateTrainingWeek: (
    updates: Partial<Omit<TrainingWeek, 'id' | 'user_id' | 'created_at' | 'updated_at'>>
  ) => Promise<void>
  createTrainingWeek: (
    data: Omit<TrainingWeek, 'id' | 'user_id' | 'created_at' | 'updated_at'>
  ) => Promise<TrainingWeek>
  getPreviousWeek: (startDate: Date) => Promise<TrainingWeek | null>
  getOrCreateTrainingWeek: (
    startDate: Date,
    defaults?: Partial<Omit<TrainingWeek, 'id' | 'user_id' | 'created_at' | 'updated_at' | 'start_date'>>
  ) => Promise<TrainingWeek>
}

const fetchTrainingWeek = async (userId: string, startDate: Date): Promise<TrainingWeek | null> => {
  const startDateString = formatDateForComparison(startDate)

  const { data, error } = await supabase
    .from('training_weeks')
    .select('*')
    .eq('user_id', userId)
    .eq('start_date', startDateString)
    .maybeSingle()

  if (error) {
    throw error
  }

  return data
}

export const useTrainingWeeks = (): UseTrainingWeeksReturn => {
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const [currentStartDate, setCurrentStartDate] = useState<Date | null>(null)

  const startDateString = useMemo(
    () => (currentStartDate ? formatDateForComparison(currentStartDate) : null),
    [currentStartDate]
  )

  const queryKey = useMemo(() => ['training-week', user?.id, startDateString], [user?.id, startDateString])

  const trainingWeekQuery = useQuery<TrainingWeek | null>({
    queryKey,
    enabled: Boolean(user?.id && currentStartDate),
    queryFn: () => fetchTrainingWeek(user!.id, currentStartDate!),
    staleTime: 60_000,
  })

  const loadTrainingWeek = useCallback(
    async (startDate: Date): Promise<void> => {
      setCurrentStartDate(startDate)

      if (!user) {
        queryClient.setQueryData(['training-week', undefined, formatDateForComparison(startDate)], null)
        return
      }

      const key = ['training-week', user.id, formatDateForComparison(startDate)]
      await queryClient.fetchQuery({
        queryKey: key,
        queryFn: () => fetchTrainingWeek(user.id, startDate),
      })
    },
    [queryClient, user]
  )

  const createTrainingWeek = useCallback(
    async (data: Omit<TrainingWeek, 'id' | 'user_id' | 'created_at' | 'updated_at'>): Promise<TrainingWeek> => {
      if (!user) {
        throw new Error('User not authenticated')
      }

      const newTrainingWeek = {
        ...data,
        user_id: user.id,
      }

      const { data: createdData, error: dbError } = await supabase
        .from('training_weeks')
        .insert(newTrainingWeek)
        .select()
        .single()

      if (dbError) {
        throw dbError
      }

      // Prime cache for the created week
      queryClient.setQueryData(
        ['training-week', user.id, formatDateForComparison(new Date(createdData.start_date))],
        createdData
      )

      return createdData
    },
    [queryClient, user]
  )

  const updateTrainingWeek = useCallback(
    async (updates: Partial<Omit<TrainingWeek, 'id' | 'user_id' | 'created_at' | 'updated_at'>>): Promise<void> => {
      if (!user || !startDateString) {
        throw new Error('User not authenticated or no training week selected')
      }

      const cached = queryClient.getQueryData<TrainingWeek | null>(queryKey)
      if (!cached) {
        throw new Error('No training week loaded to update')
      }

      const { data, error: dbError } = await supabase
        .from('training_weeks')
        .update(updates)
        .eq('id', cached.id)
        .eq('user_id', user.id)
        .select()
        .single()

      if (dbError) {
        throw dbError
      }

      queryClient.setQueryData(queryKey, data)
    },
    [queryClient, queryKey, startDateString, user]
  )

  const getPreviousWeek = useCallback(
    async (startDate: Date): Promise<TrainingWeek | null> => {
      if (!user) {
        return null
      }

      const startDateString = formatDateForComparison(startDate)

      const { data, error: dbError } = await supabase
        .from('training_weeks')
        .select('*')
        .eq('user_id', user.id)
        .lt('start_date', startDateString)
        .order('start_date', { ascending: false })
        .limit(1)
        .maybeSingle()

      if (dbError) {
        return null
      }

      return data
    },
    [user]
  )

  const getOrCreateTrainingWeek = useCallback(
    async (
      startDate: Date,
      defaults?: Partial<Omit<TrainingWeek, 'id' | 'user_id' | 'created_at' | 'updated_at' | 'start_date'>>
    ): Promise<TrainingWeek> => {
      await loadTrainingWeek(startDate)
      const key = ['training-week', user?.id, formatDateForComparison(startDate)]
      const existing = queryClient.getQueryData<TrainingWeek | null>(key)
      if (existing) {
        return existing
      }

      const previousWeek = await getPreviousWeek(startDate)

      const newTrainingWeekData = {
        start_date: formatDateForComparison(startDate),
        week_in_plan: previousWeek ? previousWeek.week_in_plan + 1 : 1,
        training_phase: previousWeek ? previousWeek.training_phase : ('build' as const),
        target_hours: previousWeek ? previousWeek.target_hours : 8.0,
        comments: null,
        ...defaults,
      }

      const created = await createTrainingWeek(newTrainingWeekData)
      queryClient.setQueryData(key, created)
      setCurrentStartDate(startDate)
      return created
    },
    [createTrainingWeek, getPreviousWeek, loadTrainingWeek, queryClient, user?.id]
  )

  return {
    trainingWeek: trainingWeekQuery.data ?? null,
    isLoading: trainingWeekQuery.isLoading || trainingWeekQuery.isFetching,
    error: trainingWeekQuery.error ? (trainingWeekQuery.error as Error).message : null,
    loadTrainingWeek,
    updateTrainingWeek,
    createTrainingWeek,
    getPreviousWeek,
    getOrCreateTrainingWeek,
  }
}
